import { createSelector } from "@reduxjs/toolkit";
import { choSearch, registQuery } from "../../functions/searchFunctions";
import { selectSelectedDeck } from "./deckSelectors";
import { LOCALES } from "../../constants";

const selectLang = (state) => state.lang;

export const selectArcanaState = (state) => state.arcanas;

export const selectArcanaEntities = (state) => selectArcanaState(state).entities;

export const selectArcanaById = createSelector(
    [selectArcanaEntities, (_, arcanaId) => arcanaId],
    (entities, arcanaId) => entities[arcanaId],
);

export const selectArcanaIds = (state) => selectArcanaState(state).ids;

export const selectArcanaFavorites = (state) => selectArcanaState(state).favorites;

export const selectArcanaFilter = (state) => selectArcanaState(state).filter;

const filterArcanas = (lang, entity, filter) => {
    if (filter.rarity.value === "all" && filter.status.value === "all") return true;
    if (filter.rarity.value === "all") return filter.status.name === entity.mainStat[lang];
    if (filter.status.value === "all") return filter.rarity.value === entity.rarity;

    return filter.status.value === entity.mainStat && filter.rarity.value === entity.rarity;
};

export const selectArcanasByFilter = createSelector(
    [selectArcanaIds, selectArcanaEntities, selectArcanaFilter, selectLang],
    (ids, entities, filter, lang) => ids.filter((id) => filterArcanas(lang.value, entities[id], filter)),
);

export const selectArcanasByQuery = createSelector(
    [selectArcanaEntities, selectLang, (_, query) => String(query).trim()],
    (entities, lang, query) => {
        if (query === "") return null;

        registQuery(lang.value, query);
        return Object.keys(entities)
            .filter((id) => {
                const searchText = lang.value === LOCALES.KO ? entities[id].searchText : entities[id].searchText_ENG;
                return choSearch(searchText);
            })
            .map(Number);
    },
);

export const selectArcanasByEventQuery = createSelector(
    [selectArcanaEntities, selectLang, (_, query) => String(query).trim()],
    (entities, lang, query) => {
        if (query === "") return null;

        registQuery(lang.value, query);
        return Object.keys(entities).filter((id) => {
            // const searchText = lang.value === LOCALES.KO ? entities[id].eventSearchText : entities[id].eventSearchText_ENG;
            const searchText = lang.value === LOCALES.KO ? entities[id].searchText : entities[id].searchText_ENG;
            return choSearch(searchText);
        });
    },
);

export const selectVisibleArcanas = createSelector(
    [selectArcanasByFilter, selectArcanasByQuery],
    (filteredArcanas, queryFilteredArcanas) => {
        if (!queryFilteredArcanas) return filteredArcanas;

        const querySet = new Set(queryFilteredArcanas);
        return filteredArcanas.filter((id) => querySet.has(id));
    },
);

export const selectIsFavorite = createSelector([selectArcanaFavorites, (_, id) => id], (favorites, id) => favorites.includes(id));

// -------------------------------------------
// deck stats
// -------------------------------------------

const ACC_STAT_TYPE_SCORE = {
    힘: 0,
    체력: 1,
    인내: 2,
    집중: 3,
    보호: 4,
    "인연 포인트 증가": 5,
    "잠재력 포인트 증가": 6,
};

const ADDITIONAL_TYPE_SCORE = {
    "초감응 훈련 효과 상승": 0,
    "훈련 효과 상승": 1,
    "감응 훈련 효과 상승": 2,
    "컨디션 효과 상승": 3,
    "훈련 출현 확률 상승": 4,
    "훈련 실패율 감소": 5,
    "감응 훈련 발생 확률 상승": 6,
};

const STAT_FLAG = {
    ACC: 0,
    ADDITIONAL: 1,
};

const STAT_REF_LIST_NAMES = {
    0: ["journeyStartStats", "supportQuestEffects"],
    1: ["trainingEffects", "telepathyEffects"],
};

const sortAccStats = (lang, stats) => {
    const order = ACC_STAT_TYPE_SCORE;
    const locale = lang === LOCALES.KO ? "ko-KR" : "en-US";

    return stats.sort((a, b) => {
        const aOrder = Number.isInteger(order[a.type[LOCALES.KO]]) ? order[a.type[LOCALES.KO]] : 100;
        const bOrder = Number.isInteger(order[b.type[LOCALES.KO]]) ? order[b.type[LOCALES.KO]] : 100;

        return aOrder - bOrder || (aOrder === 100 && bOrder === 100 ? a.type[lang].localeCompare(b.type[lang], locale) : 0);
    });
};

const sortAdditionalStats = (lang, stats) => {
    const locale = lang === LOCALES.KO ? "ko-KR" : "en-US";

    for (let i = 0; i < stats.length; i++) {
        const statObj = stats[i];

        statObj.list.sort((a, b) => {
            const aOrder = Number.isInteger(ADDITIONAL_TYPE_SCORE[a.type[LOCALES.KO]]) ? ADDITIONAL_TYPE_SCORE[a.type[LOCALES.KO]] : 100;
            const bOrder = Number.isInteger(ADDITIONAL_TYPE_SCORE[b.type[LOCALES.KO]]) ? ADDITIONAL_TYPE_SCORE[b.type[LOCALES.KO]] : 100;

            return aOrder - bOrder || (aOrder === 100 && bOrder === 100 ? a.type[lang].localeCompare(b.type[lang], locale) : 0);
        });
    }

    return stats.sort((a, b) => {
        const aOrder = Number.isInteger(ACC_STAT_TYPE_SCORE[a.name]) ? ACC_STAT_TYPE_SCORE[a.name] : 100;
        const bOrder = Number.isInteger(ACC_STAT_TYPE_SCORE[b.name]) ? ACC_STAT_TYPE_SCORE[b.name] : 100;

        return aOrder - bOrder;
    });
};

const getStatValueString = (isRate, value) => {
    return isRate ? `${(value / 100).toFixed(2)}%` : value;
};

const mergeArcanaStats = (lang, arcanas) => {
    const stats = { acc: [], additional: [] };

    arcanas.forEach((arcana) => {
        STAT_REF_LIST_NAMES[STAT_FLAG.ACC].forEach((statName) => {
            arcana.stats[statName].forEach((stat) => {
                let found = stats.acc.find((e) => e.type[LOCALES.KO] === stat.type[LOCALES.KO]);

                if (!found) {
                    found = {
                        type: stat.type,
                        level_35: stat.level_35,
                        level_40: stat.level_40,
                        level_45: stat.level_45,
                        level_50: stat.level_50,
                        isRate: stat.isRate,
                    };

                    stats.acc.push(found);
                } else {
                    found.level_35 += stat.level_35;
                    found.level_40 += stat.level_40;
                    found.level_45 += stat.level_45;
                    found.level_50 += stat.level_50;
                }
            });
        });
        STAT_REF_LIST_NAMES[STAT_FLAG.ADDITIONAL].forEach((statName) => {
            arcana.stats[statName].forEach((stat) => {
                let main = stats.additional.find((e) => e.name === arcana.mainStat);
                if (!main) {
                    main = {
                        name: arcana.mainStat,
                        list: [],
                    };

                    stats.additional.push(main);
                }

                let found = main.list.find((e) => e.type[LOCALES.KO] === stat.type[LOCALES.KO]);

                if (!found) {
                    found = {
                        type: stat.type,
                        level_35: stat.level_35,
                        level_40: stat.level_40,
                        level_45: stat.level_45,
                        level_50: stat.level_50,
                        isRate: stat.isRate,
                    };

                    main.list.push(found);
                } else {
                    found.level_35 += stat.level_35;
                    found.level_40 += stat.level_40;
                    found.level_45 += stat.level_45;
                    found.level_50 += stat.level_50;
                }
            });
        });
    });

    stats.acc.forEach((stat) => {
        stat.level_35_string = getStatValueString(stat.isRate, stat.level_35);
        stat.level_40_string = getStatValueString(stat.isRate, stat.level_40);
        stat.level_45_string = getStatValueString(stat.isRate, stat.level_45);
        stat.level_50_string = getStatValueString(stat.isRate, stat.level_50);
    });
    stats.additional.forEach((mainStat) => {
        mainStat.list.forEach((stat) => {
            stat.level_35_string = getStatValueString(stat.isRate, stat.level_35);
            stat.level_40_string = getStatValueString(stat.isRate, stat.level_40);
            stat.level_45_string = getStatValueString(stat.isRate, stat.level_45);
            stat.level_50_string = getStatValueString(stat.isRate, stat.level_50);
        });
    });

    sortAccStats(lang, stats.acc);
    sortAdditionalStats(lang, stats.additional);
    return stats;
};

export const selectSelectedDeckStats = createSelector(
    [selectArcanaEntities, selectLang, selectSelectedDeck],
    (arcanaEntities, lang, deck) => {
        if (deck.arcanaIds.length === 0) return null;

        const arcanas = deck.arcanaIds.map((id) => arcanaEntities[id]);
        return mergeArcanaStats(lang.value, arcanas);
    },
);

export const selectArcanaIdsNotInSelectedDeck = createSelector([selectVisibleArcanas, selectSelectedDeck], (arcanaIds, deck) => {
    return arcanaIds.filter((id) => !deck.arcanaIds.includes(id));
});

export const selectSelectedDeckArcanaIdsIncludesPotential = createSelector(
    [selectArcanaEntities, selectSelectedDeck],
    (arcanaEntities, deck) => {
        return deck.arcanaIds.filter((id) => arcanaEntities[id].spPotential);
    },
);
